import { useTranslation } from 'react-i18next';
import { ChevronRight, ShieldAlert, Wallet } from 'lucide-react';
import type { WalletMonitoringAddress } from '@shared/api/contracts';
import {
  formatWalletMoney,
  shouldFlattenWalletInGroup,
  type WalletFilterCatalog,
  type WalletGroupSection,
  type WalletsMonitoringFilters,
} from '@entities/wallets';
import { WalletRow } from './wallet-row';
import { cn } from '@shared/lib/utils';

interface WalletGroupCardProps {
  section: WalletGroupSection;
  filters: WalletsMonitoringFilters;
  catalog?: WalletFilterCatalog;
  expanded: boolean;
  expandedWalletIds: Set<number>;
  onToggle: () => void;
  onToggleWallet: (walletId: number) => void;
  onOpenControlMode: (address: WalletMonitoringAddress) => void;
  onToggleAml: (address: WalletMonitoringAddress) => void;
  isAmlPending: (addressId: number) => boolean;
  isControlModePending: (addressId: number) => boolean;
}

export function WalletGroupCard({
  section,
  filters,
  catalog,
  expanded,
  expandedWalletIds,
  onToggle,
  onToggleWallet,
  onOpenControlMode,
  onToggleAml,
  isAmlPending,
  isControlModePending,
}: WalletGroupCardProps) {
  const { t } = useTranslation('cabinet');
  const { group, wallets, summary } = section;
  const attentionCount = summary.highRiskAddresses;

  return (
    <div className="mb-2.5 overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3 text-left transition-colors duration-fast ease-standard hover:bg-muted/40 sm:px-4 sm:py-3.5"
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
            expanded && 'rotate-90',
          )}
        />
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Wallet className="h-4 w-4" />
          {group.name}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('wallets.group.walletCount', { count: summary.totalWallets })}
        </span>
        {attentionCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10.5px] font-semibold text-red-700">
            <ShieldAlert className="h-2.5 w-2.5" />
            {t('wallets.group.attention', { count: attentionCount })}
          </span>
        )}
        <span className="hidden flex-1 sm:block" />
        <div className="basis-[calc(50%-0.375rem)] text-left sm:basis-auto sm:min-w-[110px] sm:text-right">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('wallets.group.plan')}
          </div>
          <div className="text-sm font-semibold tabular-nums">
            {formatWalletMoney(summary.planAmount, { asset: summary.planAsset })}
          </div>
        </div>
        <div className="basis-[calc(50%-0.375rem)] text-left sm:basis-auto sm:min-w-[110px] sm:text-right">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('wallets.group.fact')}
          </div>
          <div className="text-sm font-bold tabular-nums">
            {formatWalletMoney(summary.factAmount, { asset: summary.factAsset })}
          </div>
        </div>
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div
          inert={expanded ? undefined : true}
          className={cn(
            'min-h-0 overflow-hidden',
            !expanded && '[contain-intrinsic-size:0px] [content-visibility:hidden]',
          )}
        >
          <div className="border-t border-border">
            {wallets.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">{t('wallets.group.empty')}</div>
            ) : (
              wallets.map((wallet) => {
                const flattened = shouldFlattenWalletInGroup(
                  group.name,
                  wallet.name,
                  wallets.length,
                );

                return (
                  <WalletRow
                    key={wallet.id}
                    wallet={wallet}
                    filters={filters}
                    catalog={catalog}
                    flattened={flattened}
                    expanded={flattened ? expanded : expandedWalletIds.has(wallet.id)}
                    onToggle={() => onToggleWallet(wallet.id)}
                    onOpenControlMode={onOpenControlMode}
                    onToggleAml={onToggleAml}
                    isAmlPending={isAmlPending}
                    isControlModePending={isControlModePending}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
